import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { ShieldCheck, Eye, DollarSign, CalendarCheck, ShieldAlert, Zap, AlertCircle } from 'lucide-react';

export interface PermissionNodeData {
  canViewCost?: boolean;
  canModifyPrices?: boolean;
  canBookRoom?: boolean;
  canExecuteSales?: boolean;
  shieldPII?: boolean;
  [key: string]: unknown;
}

export const PermissionNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as PermissionNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof PermissionNodeData, value: any, allUpdates?: Partial<PermissionNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const res = await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'PERMISSIONS',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[PermissionNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof PermissionNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);
  const canViewCost = Boolean(nodeData.canViewCost);
  const canModifyPrices = Boolean(nodeData.canModifyPrices);
  const canBookRoom = nodeData.canBookRoom !== false;
  const shieldPII = nodeData.shieldPII !== false;

  return (
    <div
      className={`rounded-2xl border transition-all duration-300 w-[380px] select-none group relative backdrop-blur-xl bg-gradient-to-b from-[#181105]/98 via-[#0F0B03]/98 to-[#080501]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-amber-400 ring-2 ring-amber-400/50 shadow-[0_0_35px_rgba(245,158,11,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-amber-500/40 hover:border-amber-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(245,158,11,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump Toolbar ── */}
      <div className="absolute -top-11 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-auto z-50 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-950/95 backdrop-blur-xl border border-amber-500/40 shadow-[0_0_25px_rgba(245,158,11,0.4)]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPortalData?.('permission', id);
          }}
          className="nodrag flex items-center gap-1.5 text-[11px] font-bold text-amber-300 hover:text-amber-200 transition-colors cursor-pointer"
        >
          <ShieldCheck size={13} className="text-amber-400 animate-pulse" />
          <span>RBAC Studio</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 font-mono border border-amber-500/30">
            HUD ↗
          </span>
        </button>
      </div>

      {/* ── Directional Connection Handles (Left in / Right out to Luna Core) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-permission"
        className="!bg-amber-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(245,158,11,1)]"
        title="Input: Chained Guardrail"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-permission"
        className="!bg-amber-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(245,158,11,1)]"
        title="Output to Luna Core Hub"
      />

      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-amber-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(245,158,11,0.8)]"
      />

      <div className="bg-gradient-to-r from-amber-950/80 via-orange-950/50 to-slate-950/80 px-4 py-3 border-b border-amber-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.3)]">
            <ShieldCheck size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">RBAC & Guardrails</h4>
            <p className="text-[10px] text-amber-300/70 font-mono">Zero-Trust Permission Gates</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-amber-400 animate-pulse bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
            SECURITY
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-2.5 text-xs">
        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-1">
          Capability Flags:
        </p>

        {/* Toggle 1: Wholesale Cost Viewing */}
        <div
          onClick={() => handleChange('canViewCost', !canViewCost)}
          className="nodrag flex items-center justify-between p-2 rounded-xl bg-slate-900/80 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all"
        >
          <span className="text-[11px] text-slate-200 flex items-center gap-2 font-medium">
            <Eye size={13} className="text-amber-400" /> View Wholesale Cost
          </span>
          <input
            type="checkbox"
            checked={canViewCost}
            onChange={() => {}}
            className="nodrag rounded border-white/20 bg-slate-950 text-amber-500 pointer-events-none"
          />
        </div>

        {/* Toggle 2: Modify Live Prices */}
        <div
          onClick={() => handleChange('canModifyPrices', !canModifyPrices)}
          className="nodrag flex items-center justify-between p-2 rounded-xl bg-slate-900/80 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all"
        >
          <span className="text-[11px] text-slate-200 flex items-center gap-2 font-medium">
            <DollarSign size={13} className="text-amber-400" /> Modify Live Prices
          </span>
          <input
            type="checkbox"
            checked={canModifyPrices}
            onChange={() => {}}
            className="nodrag rounded border-white/20 bg-slate-950 text-amber-500 pointer-events-none"
          />
        </div>

        {/* Toggle 3: Book Hotel / Reservation */}
        <div
          onClick={() => handleChange('canBookRoom', !canBookRoom)}
          className="nodrag flex items-center justify-between p-2 rounded-xl bg-slate-900/80 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all"
        >
          <span className="text-[11px] text-slate-200 flex items-center gap-2 font-medium">
            <CalendarCheck size={13} className="text-amber-400" /> Book Hotel / Service
          </span>
          <input
            type="checkbox"
            checked={canBookRoom}
            onChange={() => {}}
            className="nodrag rounded border-white/20 bg-slate-950 text-amber-500 pointer-events-none"
          />
        </div>

        {/* Toggle 4: Anti-Leak PII Shield */}
        <div
          onClick={() => handleChange('shieldPII', !shieldPII)}
          className="nodrag flex items-center justify-between p-2 rounded-xl bg-slate-900/80 border border-white/5 hover:border-amber-500/30 cursor-pointer transition-all"
        >
          <span className="text-[11px] text-slate-200 flex items-center gap-2 font-medium">
            <ShieldAlert size={13} className="text-rose-400" /> Anti-Leak PII Shield
          </span>
          <input
            type="checkbox"
            checked={shieldPII}
            onChange={() => {}}
            className="nodrag rounded border-white/20 bg-slate-950 text-amber-500 pointer-events-none"
          />
        </div>

      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-amber-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

PermissionNode.displayName = 'PermissionNode';
export default PermissionNode;
